import React, { useEffect, useMemo, useState } from 'react';
import { TextField, List, ListItem, IconButton, InputAdornment } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { addProviderComment, getUsersActivity } from '../../redux/ducks/provider';
import { useDispatch, useSelector } from "react-redux";
import classes from "../style.module.scss";

const ProviderComment = (props) => {
    const { npi } = props

    const [newComment, setNewComment] = useState(''); // New comment input
    const [visibility, setVisibility] = useState(0); // Segmented control state

    const dispatch = useDispatch();

    const {
        providerComment,
        usersActivity,
    } = useSelector((state) => state.provider);

    // GET /api/Client/Comment/{npi} genuinely doesn't return an author —
    // confirmed by inspecting the real response, not assumed. But
    // /api/Client/UsersActivity returns comment text *and* its author
    // together (addedBy/roleName/commentAddedDate), for the same NPI.
    // So rather than declaring this unsolvable, cross-reference each
    // comment against the activity log by matching npi + exact comment
    // text, and show the author when a match is found.
    //
    // Real caveats, not hidden: (1) this is a text match, not a proper
    // foreign key — if two different people happened to leave the
    // identical comment on the same doctor, this could attribute the
    // wrong one; (2) the activity log may not include every comment
    // ever made (older ones can fall outside it), so some comments may
    // just have no match. When that happens, this shows nothing rather
    // than a guess — same honesty rule as before.
    useEffect(() => {
        if (!usersActivity || usersActivity.length === 0) {
            dispatch(getUsersActivity());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const authorByCommentId = useMemo(() => {
        const map = {};
        const comments = providerComment?.clientComments || [];
        comments.forEach((c) => {
            const match = (usersActivity || []).find(
                (a) =>
                    String(a.npi) === String(npi) &&
                    a.feedbackType == null &&
                    a.notes === c.comment
            );
            if (match) {
                map[c.id] = {
                    name: match.addedBy,
                    role: match.roleName,
                    date: match.commentAddedDate,
                };
            }
        });
        return map;
    }, [providerComment, usersActivity, npi]);




    // Handle new comment submission
    const handleCommentSubmit = async () => {
        // Call the API
        dispatch(addProviderComment({
            npi: npi,
            comment: newComment,
            commentVisibility: parseInt(visibility)
        }));
        // Update UI with the new comment
        setNewComment('');
    };


    return (
        <>
            <span className={classes["section-label"]}>Comments</span>

            {/* Display Old Comments — confirmed via the actual live API
                response for GET /api/Client/Comment/{npi}: each item is
                only { id, comment, commentVisibility }. No author name,
                role, or date comes back from this endpoint at all —
                unlike the activity feed's UsersActivity endpoint, which
                does include addedBy/roleName/commentAddedDate. Those
                are two different endpoints with two different response
                shapes; assuming they matched was wrong, confirmed by
                the real response, not just an absence of evidence.
                Showing a permanent "Unknown user" would present a
                guess as if it were real data. Instead, authorByCommentId
                (above) cross-references the activity log for a real
                match — shown when found, omitted (not guessed) when
                not — plus a small honest tag using the one field this
                endpoint does return directly: commentVisibility. */}
            {providerComment?.clientComments?.length > 0 && (
                <List className={classes["comment-list"]}>
                    {providerComment?.clientComments?.map((item) => {
                        const author = authorByCommentId[item.id];
                        return (
                            <ListItem key={item.id} disableGutters style={{ padding: 0 }}>
                                <div className={classes["comment-bubble"]} style={{ width: "100%" }}>
                                    {author && (
                                        <div className={classes["comment-identity"]}>
                                            <span className={classes["comment-author"]}>{author.name}</span>
                                            {author.role && (
                                                <span className={classes["comment-role"]}> · {author.role}</span>
                                            )}
                                            {author.date && (
                                                <span className={classes["comment-date"]}>{author.date}</span>
                                            )}
                                        </div>
                                    )}
                                    <p className={classes["comment-text"]}>{item.comment}</p>
                                    <span className={classes["comment-visibility-tag"]}>
                                        {item.commentVisibility === 1 ? "Shared with team" : "Only visible to you"}
                                    </span>
                                </div>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            {/* Broadcast Options — real radio inputs so it's unambiguous
                this is a single exclusive choice, not two independent
                toggles (the pill-button version could visually read as
                "select any of these" at a glance). */}
            <div className={classes["visibility-choice"]} role="radiogroup" aria-label="Comment visibility">
                <label className={classes["visibility-option"]}>
                    <input
                        type="radio"
                        name="comment-visibility"
                        checked={visibility === 0}
                        onChange={() => setVisibility(0)}
                    />
                    <span>Only me</span>
                </label>
                <label className={classes["visibility-option"]}>
                    <input
                        type="radio"
                        name="comment-visibility"
                        checked={visibility === 1}
                        onChange={() => setVisibility(1)}
                    />
                    <span>For everyone</span>
                </label>
            </div>

            {/* Add New Comment */}
            <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={1}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write your comment here..."
                InputProps={{
                    endAdornment: (
                        <InputAdornment position='end'>
                            <IconButton
                                onClick={handleCommentSubmit}
                                disabled={!newComment.trim()}
                                sx={{ color: "var(--teal, #0f9d8f)" }}
                                size="small"
                            >
                                <SendIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        fontSize: '13px',
                        backgroundColor: '#fff',
                        '& fieldset': {
                            borderColor: 'var(--line-strong, rgba(60,60,67,0.22))',
                        },
                        '&:hover fieldset': {
                            borderColor: 'var(--teal, #0f9d8f)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'var(--teal, #0f9d8f)',
                            borderWidth: '1px',
                        },
                    },
                }}
            />
        </>
    );
};

export default ProviderComment;